import { useState, useRef, useMemo } from "react";
import IconCaret from "@assets/icons/caret-right.svg?react";
import ScrollShadow from "../ScrollShadow";
import Spinner from "../Spinner";
import PortalPopout from "../Popout/PortalPopout";
import Error from "../../Error";

function SearchableSelect({
	name,
	required,
	disabled,
	label,
	value,
	onChange,
	className = "",
	emptyLabel = "Type to search...",
	emptyList = null,
	data,
	isLoading = false,
	error,
	map = (o) => ({ label: o.name, value: o.id }),
	sourceFilter = null,
	fit = false,
	closeOnScroll = true,
	root,
}) {
	const [searchTerm, setSearchTerm] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);
	const inputRef = useRef(null);
	const searchInputRef = useRef(null);

	const results = isLoading || error ? [] : data || [];
	const finalResults = sourceFilter ? results.filter(sourceFilter) : results;
	const options = finalResults.map(map);

	const selectedOption = useMemo(() => {
		return options.find((opt) => String(opt.value) === String(value));
	}, [value, options]);

	const filteredOptions = searchTerm
		? options.filter((opt) =>
				opt.label.toLowerCase().includes(searchTerm.toLowerCase())
			)
		: options;
	const select = (option) => () => {
		onChange?.(inputRef.current, option.value);
		setSearchTerm("");
		setIsOpen(false);
	};

	const handleInputChange = (e) => {
		setSearchTerm(e.target.value);
		if (!isOpen && e.target.value) {
			setIsOpen(true);
		}
	};

	const handleInputFocus = () => {
		setIsOpen(true);
	};

	const handleInputBlur = () => {
		// Small delay to allow clicking on options
		setTimeout(() => {
			setIsOpen(false);
			setSearchTerm("");
		}, 200);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Escape") {
			setIsOpen(false);
			setSearchTerm("");
			searchInputRef.current?.blur();
		}
	};

	const close = () => {
		setIsOpen(false);
		setSearchTerm("");
	};

	if (error) {
		return <Error>Error loading {label} select options</Error>;
	}

	return (
		<div className={`relative min-w-34 ${className}`} ref={dropdownRef}>
			{name && (
				<input
					ref={inputRef}
					type="hidden"
					name={name}
					value={value || ""}
					required={required}
				/>
			)}
			<div className="relative">
				<input
					ref={searchInputRef}
					type="text"
					className={`w-full px-2 pr-8! pb-1! pt-2! h-12 ${
						disabled ? "bg-grey-lightest" : "bg-white"
					} ${
						required && !selectedOption
							? "border-danger"
							: "border-gray-300"
					} border rounded-xs`}
					placeholder={selectedOption?.label || emptyLabel}
					value={searchTerm}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					onBlur={handleInputBlur}
					onKeyDown={handleKeyDown}
					disabled={disabled}
				/>
				<div className="absolute top-1 left-2 text-xs text-grey-light pointer-events-none">
					{label}
				</div>
				<IconCaret
					className={`absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 text-dark transition-transform pointer-events-none ${
						isOpen ? "rotate-270" : "rotate-90"
					}`}
				/>
			</div>
			<PortalPopout
				isOpen={isOpen}
				onClose={close}
				triggerRef={dropdownRef}
				fit={fit}
				zIndex={151}
				closeOnScroll={closeOnScroll}
				root={root}
			>
				<ScrollShadow vertical wrapperClassName="max-h-60">
					{isLoading ? (
						<Spinner />
					) : filteredOptions.length > 0 ? (
						filteredOptions.map((option) => (
							<div
								key={option.value}
								className={`flex items-center gap-2 p-2 ${
									option.disabled
										? "text-grey"
										: "hover:bg-grey-lightest cursor-pointer"
								}`}
								onClick={
									option.disabled ? undefined : select(option)
								}
							>
								{option.label}
							</div>
						))
					) : emptyList ? (
						emptyList
					) : (
						<div className="p-2 text-grey text-sm">
							No options found
						</div>
					)}
				</ScrollShadow>
			</PortalPopout>
		</div>
	);
}

export default SearchableSelect;
